import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";

import { cn } from "../../lib/utils";
import { encodeClipToWav, extractPeaks, formatSeconds } from "./audio";
import { CLIP_SECONDS } from "./types";

const BUCKETS = 160;

interface AudioClipperProps {
  buffer: AudioBuffer;
  /** Called whenever the selected window changes, with the encoded clip. */
  onChange: (clip: Blob, startSeconds: number) => void;
}

/**
 * Pick the best ten seconds of a longer recording.
 *
 * The window is a fixed width the user slides, rather than two draggable handles: the
 * clip length is not theirs to choose -- a longer sample does not improve an instant
 * clone -- so offering a second handle would only invite an invalid selection.
 */
export function AudioClipper({ buffer, onChange }: AudioClipperProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const contextRef = useRef<AudioContext | null>(null);

  const [start, setStart] = useState(0);
  const [playing, setPlaying] = useState(false);

  const peaks = useMemo(() => extractPeaks(buffer, BUCKETS), [buffer]);
  const maxStart = Math.max(0, buffer.duration - CLIP_SECONDS);
  const windowSeconds = Math.min(CLIP_SECONDS, buffer.duration);

  // Re-encode on every move so the parent always holds exactly what would be uploaded,
  // rather than a promise to produce it at submit time.
  useEffect(() => {
    onChange(encodeClipToWav(buffer, start, CLIP_SECONDS), start);
  }, [buffer, start, onChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const { clientWidth, clientHeight } = canvas;
    canvas.width = clientWidth * ratio;
    canvas.height = clientHeight * ratio;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.clearRect(0, 0, clientWidth, clientHeight);

    const barWidth = clientWidth / peaks.length;
    const mid = clientHeight / 2;
    peaks.forEach((peak, index) => {
      const seconds = (index / peaks.length) * buffer.duration;
      const inWindow = seconds >= start && seconds <= start + windowSeconds;
      // The unselected audio stays visible but recedes, so the window reads as a
      // selection rather than as a crop that discarded the rest.
      ctx.fillStyle = inWindow ? "#6366f1" : "rgba(120,120,135,0.35)";
      const height = Math.max(2, peak * clientHeight * 0.9);
      ctx.fillRect(index * barWidth, mid - height / 2, Math.max(1, barWidth - 1), height);
    });
  }, [peaks, start, windowSeconds, buffer.duration]);

  const stopPlayback = useCallback(() => {
    sourceRef.current?.stop();
    sourceRef.current = null;
    void contextRef.current?.close();
    contextRef.current = null;
    setPlaying(false);
  }, []);

  useEffect(() => stopPlayback, [stopPlayback]);
  // Moving the window while it plays would leave you hearing a selection you no longer
  // have; stopping is less confusing than chasing the playhead.
  useEffect(() => {
    if (playing) stopPlayback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start]);

  const playSelection = useCallback(() => {
    if (playing) {
      stopPlayback();
      return;
    }
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.onended = () => setPlaying(false);
    source.start(0, start, windowSeconds);
    contextRef.current = ctx;
    sourceRef.current = source;
    setPlaying(true);
  }, [buffer, playing, start, windowSeconds, stopPlayback]);

  const seekFromPointer = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track || maxStart === 0) return;
      const { left, width } = track.getBoundingClientRect();
      const fraction = Math.max(0, Math.min(1, (clientX - left) / width));
      // Centre the window on the pointer: dragging feels like moving the selection,
      // not like setting its left edge.
      setStart(Math.max(0, Math.min(maxStart, fraction * buffer.duration - windowSeconds / 2)));
    },
    [buffer.duration, maxStart, windowSeconds],
  );

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    seekFromPointer(event.clientX);
  };
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.buttons === 1) seekFromPointer(event.clientX);
  };

  const leftPercent = buffer.duration ? (start / buffer.duration) * 100 : 0;
  const widthPercent = buffer.duration ? (windowSeconds / buffer.duration) * 100 : 100;

  return (
    <div className="space-y-3">
      <div
        ref={trackRef}
        className={cn(
          "relative h-28 w-full overflow-hidden rounded-xl border border-line bg-soft",
          maxStart > 0 ? "cursor-ew-resize" : "cursor-default",
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
      >
        <canvas ref={canvasRef} className="h-full w-full" />
        <div
          className="pointer-events-none absolute inset-y-0 rounded-md border-2 border-brand bg-brand/10"
          style={{ left: `${leftPercent}%`, width: `${widthPercent}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={playSelection}
          className="inline-flex items-center gap-2 rounded-lg border border-line px-3 py-1.5 font-medium text-ink hover:bg-soft"
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          {playing ? "Stop" : "Play selection"}
        </button>
        <span className="text-ink-2">
          {formatSeconds(start)} – {formatSeconds(start + windowSeconds)} of{" "}
          {formatSeconds(buffer.duration)}
        </span>
      </div>

      {maxStart === 0 && (
        <p className="text-xs text-ink-3">
          This recording is {formatSeconds(buffer.duration)} long, so all of it is used.
        </p>
      )}
    </div>
  );
}
